import React, { useState, useEffect } from "react";
import { Container, Grid, Box, Typography } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import {
  CustomTitle,
  CustomLabel,
} from "../../components/Custom/CustomTypography";
import CustomTabs from "../../components/Custom/CustomTabs";
import { useNavigate } from "react-router-dom";
import TaskCard from "../../components/Programs/TaskCard";

export default function ProgramInput() {
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [completedTasksTab, setCompletedTasksTab] = useState(0);

  const [tasks, setTasks] = useState({
    upcoming: { recommended: [], others: [] },
    ongoing: { recommended: [], others: [] },
    completed: { recommended: [], others: [] },
    underVerification: { recommended: [], others: [] },
    declined: { recommended: [], others: [] },
    rejected: { recommended: [], others: [] },
  });

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await api.get("/programs/farmer-tasks");
        segregateTasks(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching tasks:", error);
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  const segregateTasks = (data) => {
    const addProgramName = (tasks, programName) => {
      return tasks.map((task) => ({ ...task, program_name: programName }));
    };

    const recommendedUpcomingTasks = data.recommended_programs.flatMap(
      (program) => addProgramName(program.upcoming_tasks, program.program_name)
    );
    const otherUpcomingTasks = data.other_programs.flatMap((program) =>
      addProgramName(program.upcoming_tasks, program.program_name)
    );
    const recommendedOngoingTasks = data.recommended_programs.flatMap(
      (program) => addProgramName(program.ongoing_tasks, program.program_name)
    );
    const otherOngoingTasks = data.other_programs.flatMap((program) =>
      addProgramName(program.ongoing_tasks, program.program_name)
    );
    const recommendedCompletedTasks = data.recommended_programs.flatMap(
      (program) => addProgramName(program.accepted_tasks, program.program_name)
    );
    const otherCompletedTasks = data.other_programs.flatMap((program) =>
      addProgramName(program.accepted_tasks, program.program_name)
    );
    const recommendedUnderVerificationTasks = data.recommended_programs.flatMap(
      (program) =>
        addProgramName(program.under_verification_tasks, program.program_name)
    );
    const otherUnderVerificationTasks = data.other_programs.flatMap((program) =>
      addProgramName(program.under_verification_tasks, program.program_name)
    );
    const recommendedDeclinedTasks = data.recommended_programs.flatMap(
      (program) => addProgramName(program.declined_tasks, program.program_name)
    );
    const otherDeclinedTasks = data.other_programs.flatMap((program) =>
      addProgramName(program.declined_tasks, program.program_name)
    );
    const recommendedRejectedTasks = data.recommended_programs.flatMap(
      (program) => addProgramName(program.rejected_tasks, program.program_name)
    );
    const otherRejectedTasks = data.other_programs.flatMap((program) =>
      addProgramName(program.rejected_tasks, program.program_name)
    );

    setTasks({
      upcoming: {
        recommended: recommendedUpcomingTasks,
        others: otherUpcomingTasks,
      },
      ongoing: {
        recommended: recommendedOngoingTasks,
        others: otherOngoingTasks,
      },
      completed: {
        recommended: recommendedCompletedTasks,
        others: otherCompletedTasks,
      },
      underVerification: {
        recommended: recommendedUnderVerificationTasks,
        others: otherUnderVerificationTasks,
      },
      declined: {
        recommended: recommendedDeclinedTasks,
        others: otherDeclinedTasks,
      },
      rejected: {
        recommended: recommendedRejectedTasks,
        others: otherRejectedTasks,
      },
    });
  };

  const handleCardClick = (taskId) => {
    navigate(`/farmer/program-task-details/${taskId}`);
  };

  const handleOngoingCardClick = (taskId) => {
    navigate(`/farmer/program-task-status-update/${taskId}`);
  };

  const renderTaskList = (title, tasks, handleCardClick) => (
    <>
      {tasks.length > 0 ? (
        <Grid item xs={12} sx={{ marginTop: "20px" }}>
          <CustomLabel>{t(title)}</CustomLabel>
          <Box mt={1}>
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                t={t}
                handleView={
                  handleCardClick ? () => handleCardClick(task.id) : undefined
                }
              />
            ))}
          </Box>
        </Grid>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "auto",
              height: "30vh",
            }}
          >
            <Typography variant="subtitle1">
              {t(`No ${title} Tasks Found`)}
            </Typography>
          </Box>
        </>
      )}
    </>
  );

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="xs">
          <CustomTitle>{t("Program Tasks")}</CustomTitle>
          <div style={{ marginTop: "10px" }}></div>

          <CustomTabs
            numTabs={3}
            tabTitles={[t("Upcoming"), t("Ongoing"), t("Completed")]}
            textColors={["black", "black", "black", "black"]}
            onTabChange={setActiveTab}
          />

          {activeTab === 0 && (
            <Grid container spacing={2}>
              {renderTaskList(
                "Recommended",
                tasks.upcoming.recommended,
                handleCardClick
              )}
              {renderTaskList("Others", tasks.upcoming.others, handleCardClick)}
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={2}>
              {renderTaskList(
                "Recommended",
                tasks.ongoing.recommended,
                handleOngoingCardClick
              )}
              {renderTaskList(
                "Others",
                tasks.ongoing.others,
                handleOngoingCardClick
              )}
            </Grid>
          )}

          {activeTab === 2 && (
            <Box mt={2}>
              <CustomTabs
                numTabs={3}
                tabTitles={[t("Pending"), t("Accepted"), t("Rejected")]}
                textColors={["black", "black", "black"]}
                onTabChange={setCompletedTasksTab}
              />

              {completedTasksTab === 0 && (
                <Grid container spacing={2}>
                  {renderTaskList(
                    "Pending",
                    tasks.underVerification.recommended
                  )}
                  {renderTaskList("Others", tasks.underVerification.others)}
                </Grid>
              )}

              {completedTasksTab === 1 && (
                <Grid container spacing={2}>
                  {renderTaskList("Accepted", tasks.completed.recommended)}
                  {renderTaskList("Others", tasks.completed.others)}
                </Grid>
              )}

              {completedTasksTab === 2 && (
                <Grid container spacing={2}>
                  {renderTaskList("Rejected", tasks.rejected.recommended)}
                  {renderTaskList("Others", tasks.rejected.others)}
                </Grid>
              )}
            </Box>
          )}
        </Container>
      </LoaderContainer>
    </>
  );
}
